import { API_BASE_URL } from "./config";
import { supabase } from "@/lib/supabase";
import type { AuthUser } from "@/store/authStore";

export type SessionResponse = {
  user: AuthUser & { issued_at?: string | null };
};

/**
 * Fetch session from backend using Supabase JWT token.
 * This is used for backend API calls that need to verify the user.
 * Includes X-Role-Context header if a role was selected during login.
 */
export async function fetchSession(accessToken: string): Promise<SessionResponse> {
  // Read selected role from localStorage (set during login)
  const selectedRole = localStorage.getItem("selectedRole");
  
  const headers: HeadersInit = {
    Authorization: `Bearer ${accessToken}`,
  };
  
  // Add role context header if role was selected
  if (selectedRole) {
    headers["X-Role-Context"] = selectedRole;
  }
  
  const response = await fetch(`${API_BASE_URL}/v1/auth/me`, {
    headers,
  });

  if (!response.ok) {
    // Check if it's a 403 Forbidden (role mismatch)
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || "You do not have permission to access this role.");
      (error as any).status = 403;
      throw error;
    }
    throw new Error("Session expired");
  }

  return response.json();
}

/**
 * Get current user from Supabase Auth.
 * This is a convenience function that uses Supabase directly.
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  
  if (error) {
    throw error;
  }
  
  return user;
}

